/**
 * The force exit: close a position once it has been open for a set time, whatever the
 * stop and target are doing.
 *
 * Backtests have carried `analyzeTrades.forceExit` for years in two shapes, `time` and
 * `candles`. Only `time` crosses to a live alert:
 *
 * - The exit engine sweeps deadlines computed from `entryTime`, which needs no chart.
 *   A candle count would need the trade's own trigger chart, and no backtest has ever
 *   used that shape — measured on production, 3,347 backtests carry a time exit and
 *   zero carry a candle one.
 * - The deadline is wall clock. A rule that falls while the market is shut closes the
 *   position when it reopens, which is what the backtester does too.
 */

/**
 * The live half of the stored shape, narrowed.
 *
 * The canonical backtest types are `timeForceExit` and `ForceExit` in
 * `contexts/ApplicationSuiteProvider.tsx` (lines 109-120), where `timeframe` is a plain
 * `string` and `candles` is a sibling of `time`. This one exists to say what a *live*
 * rule is: the time shape only, with `timeframe` narrowed to the units the engine
 * honours. A third, unused `ForceExitTime` also sits in the dead
 * `layout/settingsMenu/ForceExitSettings.tsx`.
 */
export type ForceExitTime = {
  periodicity: number
  timeframe: LiveForceExitTimeframe
}

/**
 * The units the live engine honours. The backtest form offers minutes and hours; day
 * and week are accepted because the AI strategy agent can write them, and `month` is
 * deliberately absent — the engine refuses it rather than guessing a month's length.
 */
export const LIVE_FORCE_EXIT_TIMEFRAMES = ['minute', 'hour', 'day', 'week'] as const

export type LiveForceExitTimeframe = (typeof LIVE_FORCE_EXIT_TIMEFRAMES)[number]

const isLiveTimeframe = (value: unknown): value is LiveForceExitTimeframe =>
  LIVE_FORCE_EXIT_TIMEFRAMES.includes(value as LiveForceExitTimeframe)

/**
 * The rule a live alert can actually run, or undefined. An unusable one is dropped
 * rather than carried: an alert that stores a rule nothing honours reads to the trader
 * as a position that will close itself when it never will.
 */
export const liveForceExitTime = (analyzeTrades: any): ForceExitTime | undefined => {
  const time = analyzeTrades?.forceExit?.time
  const periodicity = Number(time?.periodicity)
  if (!Number.isFinite(periodicity) || periodicity <= 0) return undefined
  if (!isLiveTimeframe(time?.timeframe)) return undefined
  return { periodicity, timeframe: time.timeframe }
}

/** "10 minutes", "1 hour" — for the read-only views. */
export const forceExitLabel = (analyzeTrades: any): string | undefined => {
  const time = liveForceExitTime(analyzeTrades)
  if (!time) return undefined
  return `${time.periodicity} ${time.timeframe}${time.periodicity === 1 ? '' : 's'}`
}
